import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import Password from "../Login/Password";
import Swal from "sweetalert2";
import { useFavoritos } from "../../favoritos";

// Cualquier pantalla se puede marcar como favorita desde la barra lateral (las que no tienen tarjeta en los
// menus, p. ej. /OrdenesCompraCreate). Solo rutas internas simples, como las valida el back.
const RUTA_FAVORITO = /^\/[A-Za-z0-9/_-]*$/;
const SIN_FAVORITO = ["/", "/Inicio", "/Login", "/Logout"];
// Nombre sugerido: "/OrdenesCompraCreate" -> "Crear Ordenes Compra", "/ListadoKardex" -> "Listado Kardex"
const nombreSugerido = (ruta) => {
  const base = (ruta.split("/").filter(Boolean)[0] || ruta).replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
  return / Create$/.test(base) ? `Crear ${base.replace(/ Create$/, "")}` : base;
};

const NAV_SECTIONS = [
  {
    section: "Operaciones",
    items: [
      { label: "Modelos",      icon: "fas fa-box",            href: "/MenuArticulos",   perm: "menu_articulos" },
      { label: "Compras",      icon: "fas fa-shopping-cart",     href: "/MenuCompras",     perm: "menu_compras"   },
      { label: "Ventas",       icon: "fas fa-cash-register",     href: "/MenuVentas",      perm: "menu_ventas"    },
      // { label: "Tiendas",      icon: "fas fa-store",             href: "/MenuTiendas",     perm: "menuTiendas"    },
    ],
  },
  {
    section: "Inventarios",
    items: [
      { label: "Inventarios",  icon: "fas fa-boxes",             href: "/MenuInventarios", perm: "menu_inventarios" },
    ],
  },
  {
    section: "Finanzas",
    items: [
      { label: "Admin",        icon: "fas fa-hand-holding-usd",  href: "/MenuAdmin",       perm: "configAdmin"      },
      { label: "CXP",          icon: "fas fa-file-invoice",      href: "/MenuCXP",         perm: "pagoProveedores"  },
      { label: "Gastos",       icon: "fas fa-wallet",            href: "/MenuGastos",      perm: "menu_admin"       },
    ],
  },
  {
    section: "Recursos",
    items: [
      { label: "Empleados",    icon: "fas fa-users",             href: "/MenuNominas",     perm: "menu_empleados"   },
    ],
  },
  {
    section: "Análisis",
    items: [
      { label: "Reportes",     icon: "fas fa-chart-bar",         href: "/MenuReportes",    perm: "menu_reportes"    },
    ],
  },
  {
    section: "Admin",
    items: [
      { label: "Usuarios",     icon: "fas fa-user-cog",          href: "/MenuUsuarios",    perm: "menu_usuarios"    },
      { label: "Logs",         icon: "fas fa-clipboard-list",    href: "/MenuLogs",        perm: "menu_logs"        },
    ],
  },
];

const MELI_ONLY_SECTIONS = [
  {
    section: "Operaciones",
    items: [
      { label: "Tiendas", icon: "fas fa-store", href: "/MenuTiendas", perm: "menuTiendas" },
    ],
  },
];

const Header = () => {
  const { isAuth, user } = useContext(AuthContext);
  const { favoritos, esFavorito, alternar, quitar } = useFavoritos(isAuth ? user : null);

  const AREAS_MELI      = process.env.REACT_APP_AREAS_MELI;
  const AREAS_MELI_NOVA = process.env.REACT_APP_AREAS_MELI_NOVA;
  const AREAS_AMAZON    = process.env.REACT_APP_AREAS_AMAZON;
  const AREAS_AMAZON_YOU = process.env.REACT_APP_AREAS_AMAZON_YOU;
  const AREAS_TIKTOK    = process.env.REACT_APP_AREAS_TIKTOK;

  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [modalPassword, setModalPassword] = useState(false);
  const [userMenuOpen,  setUserMenuOpen]  = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true"
  );

  const currentPath = window.location.pathname;

  const isMeliAmazonUser =
    user &&
    (user.areas === AREAS_MELI ||
      user.areas === AREAS_MELI_NOVA ||
      user.areas === AREAS_AMAZON ||
      user.areas === AREAS_AMAZON_YOU ||
      user.areas === AREAS_TIKTOK);

  const sections = isMeliAmazonUser ? MELI_ONLY_SECTIONS : NAV_SECTIONS;

  const puedeMarcar = RUTA_FAVORITO.test(currentPath) && !SIN_FAVORITO.includes(currentPath);
  const actualFavorito = esFavorito(currentPath);
  const marcarActual = async () => {
    if (actualFavorito) return quitar(currentPath);
    const { value } = await Swal.fire({
      title: "Agregar a favoritos",
      text: "Nombre con el que sale en el menú",
      input: "text",
      inputValue: nombreSugerido(currentPath),
      showCancelButton: true,
      confirmButtonText: "Agregar",
      cancelButtonText: "Cancelar",
      inputValidator: (v) => (!String(v || "").trim() ? "Ponle un nombre" : undefined),
    });
    if (!value) return;
    alternar({
      label: String(value).trim().slice(0, 80),
      icon: /Create$/i.test(currentPath.split("/").filter(Boolean)[0] || "") ? "fas fa-plus-circle" : "fas fa-bookmark",
      color: "",
      href: currentPath,
    });
  };

  useEffect(() => {
    if (!isAuth) return;
    document.body.classList.add("has-sidebar");
    if (collapsed) document.body.classList.add("sidebar-collapsed");
    return () => {
      document.body.classList.remove("has-sidebar");
      document.body.classList.remove("sidebar-collapsed");
    };
  }, [isAuth]); // eslint-disable-line

  if (!isAuth) return null;

  const hasPerm = (perm) => {
    if (!user) return false;
    return !!user[perm];
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar-collapsed", next);
    if (next) {
      document.body.classList.add("sidebar-collapsed");
      setUserMenuOpen(false);
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
  };

  return (
    <>
      {/* Mobile top bar */}
      <div className="sidebar-topbar d-lg-none">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <i className="fas fa-bars" />
        </button>
        <a href="/Inicio">
          <img src={process.env.PUBLIC_URL + "/logoInstitucion.png"} height="34" alt="logo" />
        </a>
      </div>

      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={[
          "app-sidebar",
          sidebarOpen ? "open"      : "",
          collapsed   ? "collapsed" : "",
        ].filter(Boolean).join(" ")}
      >
        {/* Brand */}
        <div className="sidebar-brand">
          <a href="/Inicio" className="sidebar-logo-link">
            <img
              src={process.env.PUBLIC_URL + "/logoInstitucion.png"}
              alt="logo"
              style={{ maxHeight: 44, width: "auto" }}
            />
          </a>
          <button
            className="sidebar-collapse-btn"
            onClick={toggleCollapse}
            title={collapsed ? "Expandir" : "Comprimir"}
          >
            <i className={`fas fa-chevron-${collapsed ? "right" : "left"}`} />
          </button>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {/* Favoritos del usuario: se marcan con la estrella de las tarjetas de cada menu o con
              "Agregar esta pantalla" (cualquier pantalla, p. ej. los Create) */}
          {(favoritos.length > 0 || puedeMarcar) && (
            <div className="sidebar-section-group">
              <div className="sidebar-section">Favoritos</div>
              {favoritos.map((f) => (
                <a
                  key={f.href}
                  href={f.href}
                  className={`sidebar-item${currentPath === f.href ? " active" : ""}`}
                  title={collapsed ? f.label : ""}
                >
                  <i className={f.icon || "fas fa-star"} style={f.color ? { color: f.color } : undefined} />
                  <span className="sidebar-label" style={{ flex: 1 }}>{f.label}</span>
                  {!collapsed && (
                    <span
                      role="button"
                      title="Quitar de favoritos"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); quitar(f.href); }}
                      style={{ opacity: 0.45, fontSize: "0.75rem", padding: "0 2px" }}
                    >
                      <i className="fas fa-times" style={{ width: "auto", margin: 0 }} />
                    </span>
                  )}
                </a>
              ))}
              {puedeMarcar && (
                <div
                  role="button"
                  className="sidebar-item sidebar-item-sm"
                  onClick={marcarActual}
                  title={collapsed ? (actualFavorito ? "Quitar esta pantalla de favoritos" : "Agregar esta pantalla a favoritos") : ""}
                  style={{ cursor: "pointer", opacity: 0.8 }}
                >
                  <i className={actualFavorito ? "fas fa-star" : "far fa-star"} style={actualFavorito ? { color: "#f59e0b" } : undefined} />
                  <span className="sidebar-label">{actualFavorito ? "Quitar esta pantalla" : "Agregar esta pantalla"}</span>
                </div>
              )}
            </div>
          )}
          {sections.map((sec) => {
            const visibleItems = sec.items.filter((i) => hasPerm(i.perm));
            if (visibleItems.length === 0) return null;

            return (
              <div key={sec.section} className="sidebar-section-group">
                <div className="sidebar-section">{sec.section}</div>

                {visibleItems.map((item) => {
                  const isActive = currentPath === item.href;
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={`sidebar-item${isActive ? " active" : ""}`}
                      title={collapsed ? item.label : ""}
                    >
                      <i className={item.icon} />
                      <span className="sidebar-label">{item.label}</span>
                    </a>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="sidebar-footer">
          <div
            className="sidebar-item"
            onClick={() => !collapsed && setUserMenuOpen(!userMenuOpen)}
            title={collapsed ? (user.nombre || user.email || "Usuario") : ""}
            style={{ justifyContent: collapsed ? "center" : "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <i className="fas fa-user-circle" />
              <span className="sidebar-label" style={{ fontSize: "0.78rem" }}>
                {user.nombre || user.email || "Usuario"}
              </span>
            </div>
            {!collapsed && (
              <i className={`fas fa-chevron-${userMenuOpen ? "down" : "up"} fa-xs`} style={{ opacity: 0.5 }} />
            )}
          </div>

          {userMenuOpen && !collapsed && (
            <div className="sidebar-submenu">
              <div
                className="sidebar-item sidebar-item-sm"
                onClick={() => { setModalPassword(true); setUserMenuOpen(false); }}
              >
                <i className="fas fa-lock" />
                <span>Cambiar contraseña</span>
              </div>
              <a href="/Logout" className="sidebar-item sidebar-item-sm" style={{ color: "#f87171" }}>
                <i className="fas fa-sign-out-alt" />
                <span>Cerrar sesión</span>
              </a>
            </div>
          )}
        </div>
      </aside>

      <Modal size="sm" isOpen={modalPassword} toggle={() => setModalPassword(false)}>
        <ModalHeader toggle={() => setModalPassword(false)}>
          Cambio de contraseña
        </ModalHeader>
        <ModalBody>
          <Password />
        </ModalBody>
      </Modal>
    </>
  );
};

export default Header;
